import type { StandardSchemaV1 } from '@standard-schema/spec'
import { SchemaError } from '@standard-schema/utils'
import { parse as didParse } from 'iso-did'
import { Delegation } from './delegation.js'
import { Invocation } from './invocation.js'
import type { CapabilityOptions, CapabilityDelegateOptions, CapabilityInvokeOptions, IsRevoked, DidResolver, VerifierResolver, CborObject } from './types.js'

export class Capability<
  Schema extends StandardSchemaV1 = StandardSchemaV1,
  Cmd extends string = string
> {
    schema:Schema
    cmd:Cmd
    isRevoked?:IsRevoked
    didResolver?:DidResolver
    verifierResolver:VerifierResolver

    constructor (options:CapabilityOptions<Schema, Cmd>) {
        this.schema = options.schema
        this.cmd = options.cmd
        this.isRevoked = options.isRevoked
        this.didResolver = options.didResolver
        this.verifierResolver = options.verifierResolver
    }

    static from<Schema extends StandardSchemaV1, Cmd extends string = string> (
        options:CapabilityOptions<Schema, Cmd>
    ):Capability<Schema, Cmd> {
        return new Capability(options)
    }

    async validate (args:StandardSchemaV1.InferInput<Schema>) {
        const result = await this.schema['~standard'].validate(args)
        return result
    }

    /**
     * Invoke this capability
     */
    async invoke (options:CapabilityInvokeOptions<Schema>):Promise<Invocation> {
        const result = await this.schema['~standard'].validate(options.args)
        if (result.issues) {
            throw new SchemaError(result.issues)
        }

        let proofs:Delegation[] = []
        const iss = didParse(options.iss.did)
        if (iss.did !== options.sub) {
            proofs = await options.store.chain({
                cmd: this.cmd,
                sub: options.sub,
                aud: options.iss.did,
                args: result.value as CborObject,
            })
        }
        return await Invocation.create({
            ...options,
            args: result.value as CborObject,
            cmd: this.cmd,
            prf: proofs,
            verifierResolver: this.verifierResolver,
            didResolver: this.didResolver,
            isRevoked: this.isRevoked,
        })
    }

    /**
     * Create a delegation for this capability
     */
    async delegate (options:CapabilityDelegateOptions<Schema>):Promise<Delegation> {
        const dlg = await Delegation.create({ ...options, cmd: this.cmd })
        await options.store.add([dlg])
        return dlg
    }
}
